import React, { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { DP, float } from "./HomeStyles";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const morphOrb = keyframes`
  0%,100% { border-radius: 62% 38% 46% 54% / 60% 44% 56% 40%; }
  50%      { border-radius: 30% 70% 60% 40% / 40% 62% 38% 60%; }
`;
const glowBreath = keyframes`
  0%,100% { box-shadow: 0 4px 18px rgba(229,57,53,0.3); }
  50%      { box-shadow: 0 4px 32px rgba(229,57,53,0.55), 0 0 0 6px rgba(229,57,53,0.08); }
`;
const shimmerSweep = keyframes`
  0%   { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(220%)  skewX(-18deg); }
`;
const borderShift = keyframes`
  0%   { background-position: 0%   50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0%   50%; }
`;

const locationAccents = [
  { accent: DP.blue,   accentBg: "rgba(21,101,192,0.07)"  },
  { accent: "#7B1FA2", accentBg: "rgba(123,31,162,0.07)"  },
];

const EmergencyBanner: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t, isRTL } = useLanguage();
  const te = t.home.emergency;

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ background: `linear-gradient(160deg, #F0F7FF 0%, #E3F2FD 55%, #F8FBFF 100%)`, py: { xs: 9, md: 14 }, position: "relative", overflow: "hidden" }}>

      {/* Decorative orbs */}
      <Box sx={{ position: "absolute", top: "-10%", right: "-6%", width: { xs: 280, md: 460 }, height: { xs: 280, md: 460 }, background: `radial-gradient(circle, rgba(21,101,192,0.08) 0%, transparent 70%)`, animation: `${morphOrb} 14s ease-in-out infinite`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: "-15%", left: "-8%", width: { xs: 220, md: 360 }, height: { xs: 220, md: 360 }, background: `radial-gradient(circle, rgba(66,165,245,0.09) 0%, transparent 70%)`, animation: `${morphOrb} 18s ease-in-out infinite reverse`, pointerEvents: "none" }} />

      {/* Grid texture */}
      <Box sx={{ position: "absolute", inset: 0, opacity: 0.022, pointerEvents: "none", backgroundImage: `linear-gradient(rgba(21,101,192,1) 1px, transparent 1px), linear-gradient(90deg, rgba(21,101,192,1) 1px, transparent 1px)`, backgroundSize: "52px 52px" }} />

      {/* Floating paws */}
      {["🐾", "🐾", "🐾"].map((p, i) => (
        <Box key={i} sx={{ position: "absolute", top: `${18 + i * 28}%`, left: `${3 + i * 7}%`, fontSize: { xs: "1.4rem", md: "1.8rem" }, opacity: 0.055, animation: `${float} ${6 + i * 2}s ease-in-out infinite`, animationDelay: `${i * 1.4}s`, pointerEvents: "none" }}>{p}</Box>
      ))}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">

          {/* Left: Emergency copy */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ animation: visible ? `${slideInLeft} 0.8s cubic-bezier(0.22,1,0.36,1) both` : "none" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, backgroundColor: "rgba(229,57,53,0.08)", border: "1px solid rgba(229,57,53,0.22)", borderRadius: "50px", px: 2.2, py: 0.9, mb: 3.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#E53935", animation: "pdot 1.6s ease-in-out infinite", "@keyframes pdot": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.35, transform: "scale(0.65)" } } }} />
              <Typography sx={{ color: "#E53935", fontWeight: 700, fontSize: "0.78rem", letterSpacing: isRTL ? "0.04em" : "0.12em" }}>
                {te.badge}
              </Typography>
            </Box>

            <Typography variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.8rem" }, fontWeight: 800, color: DP.navy, lineHeight: 1.15, mb: 2.5, letterSpacing: "-0.03em" }}>
              {te.heading}{" "}
              <Box component="span" sx={{ background: `linear-gradient(135deg, ${DP.blue}, #42A5F5)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {te.headingHighlight}
              </Box>
            </Typography>

            <Typography sx={{ fontSize: "1.02rem", color: "rgba(10,22,40,0.6)", lineHeight: 1.85, mb: 5.5, maxWidth: 480 }}>
              {te.body}
            </Typography>

            <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", animation: visible ? `${fadeUp} 0.6s ease 0.45s both` : "none" }}>
              {/* Call Now */}
              <Box component="a" href="tel:+96170886078" aria-label={te.callNow} sx={{ display: "inline-flex", alignItems: "center", gap: 1.4, backgroundColor: "#E53935", color: "#fff", px: 3.5, py: 1.6, fontWeight: 700, fontSize: "0.98rem", borderRadius: "50px", textDecoration: "none", position: "relative", overflow: "hidden", animation: `${glowBreath} 2.8s ease-in-out infinite`, transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), background-color 0.25s ease", "&::after": { content: '""', position: "absolute", top: 0, left: 0, width: "45%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)", transform: "translateX(-120%) skewX(-18deg)", transition: "none", pointerEvents: "none" }, "&:hover::after": { animation: `${shimmerSweep} 0.65s ease forwards` }, "&:hover": { backgroundColor: "#C62828", transform: "translateY(-3px) scale(1.03)", boxShadow: "0 10px 36px rgba(229,57,53,0.45)" } }}>
                <PhoneIcon sx={{ fontSize: "1.15rem" }} />
                {te.callNow}
              </Box>

              {/* Contact Us — animated gradient border */}
              <Box onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} sx={{ display: "inline-flex", alignItems: "center", gap: 1.2, px: 3.5, py: 1.6, fontWeight: 700, fontSize: "0.98rem", color: DP.blue, borderRadius: "50px", cursor: "pointer", position: "relative", background: `linear-gradient(#F0F7FF, #F0F7FF) padding-box, linear-gradient(120deg, ${DP.blue}, #42A5F5, #90CAF9, ${DP.blue}) border-box`, border: "2px solid transparent", backgroundSize: "200% 200%", animation: `${borderShift} 3s ease infinite`, overflow: "hidden", transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, color 0.25s ease", "&::after": { content: '""', position: "absolute", top: 0, left: 0, width: "45%", height: "100%", background: `linear-gradient(90deg, transparent, rgba(21,101,192,0.1), transparent)`, transform: "translateX(-120%) skewX(-18deg)" }, "&:hover::after": { animation: `${shimmerSweep} 0.65s ease forwards` }, "&:hover": { transform: "translateY(-3px) scale(1.03)", boxShadow: `0 10px 36px rgba(21,101,192,0.2)`, color: "#0D47A1", background: `linear-gradient(rgba(21,101,192,0.06), rgba(21,101,192,0.06)) padding-box, linear-gradient(120deg, ${DP.blue}, #42A5F5, #90CAF9, ${DP.blue}) border-box`, backgroundSize: "200% 200%" } }}>
                {te.contactUs}
                <ArrowForwardIcon sx={{ fontSize: "1rem", transition: "transform 0.25s ease", transform: isRTL ? "scaleX(-1)" : "none" }} />
              </Box>
            </Box>
          </Grid>

          {/* Right: Location cards */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ animation: visible ? `${slideInRight} 0.8s cubic-bezier(0.22,1,0.36,1) 0.2s both` : "none" , mt:10 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {te.locations.map((loc, i) => (
                <Box key={i} sx={{ backgroundColor: "#FFFFFF", border: `1.5px solid rgba(21,101,192,0.1)`, borderRadius: "20px", p: 3, boxShadow: "0 4px 20px rgba(21,101,192,0.07)", transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)", animation: visible ? `${fadeUp} 0.6s ease ${0.3 + i * 0.15}s both` : "none", "&:hover": { transform: "translateY(-5px)", boxShadow: "0 16px 48px rgba(21,101,192,0.14)", borderColor: `${locationAccents[i].accent}45` } }}>
                  <Typography sx={{ color: DP.navy, fontWeight: 800, fontSize: "1.05rem", mb: 2.2, textAlign: { xs: "center", md: "inherit" } }}>{loc.name}</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.3, mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <AccessTimeIcon sx={{ color: DP.blue, fontSize: "1rem", flexShrink: 0 }} />
                      <Typography sx={{ color: "rgba(10,22,40,0.62)", fontSize: "0.9rem" }}>{loc.hours}</Typography>
                    </Box>
                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <PhoneIcon sx={{ color: DP.blue, fontSize: "1rem", flexShrink: 0 }} />
                      <Typography sx={{ color: "rgba(10,22,40,0.62)", fontSize: "0.9rem" }}>04 541 965</Typography>
                    </Box> */}
                  </Box>
                  {loc.mapUrl && (
                    <Box
                      component="a"
                      href={loc.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: { xs: "flex", md: "inline-flex" }, width: "fit-content", mx: { xs: "auto", md: 0 }, alignItems: "center", gap: 0.8, px: 2, py: 0.9, borderRadius: "30px", backgroundColor: locationAccents[i].accentBg, border: `1px solid ${locationAccents[i].accent}40`, textDecoration: "none", cursor: "pointer", transition: "all 0.25s ease", "&:hover": { backgroundColor: locationAccents[i].accent, "& .tag-text": { color: "#fff" } } }}
                    >
                      <Typography className="tag-text" sx={{ color: locationAccents[i].accent, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", transition: "color 0.25s ease" }}>
                        {loc.tag}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: "0.75rem", color: locationAccents[i].accent, transition: "color 0.25s ease" }} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default EmergencyBanner;
